import { cn } from "@nextui-org/react";
import { faBrightness, faMoon, faWindow, IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Breakpoint, useBreakpoint } from "cenero-ui-library";
import { useSideMenuStore } from "@/store/SideMenuStore";
import { useTheme } from "@/hooks/useTheme";
import { Theme } from "@/providers/ThemeProvider";

// ---------------------- Interfaces

interface ITheme {
  name: Theme;
  icon: IconDefinition;
}

// ---------------------- Variables

const themes: ITheme[] = [
  {
    name: "light",
    icon: faBrightness,
  },
  {
    name: "system",
    icon: faWindow,
  },
  {
    name: "dark",
    icon: faMoon,
  },
];

// ---------------------- Component

const ThemeSelector = () => {
  // --------- Variables / Hooks

  const { theme, setTheme } = useTheme();

  const { isOpen } = useSideMenuStore();

  const breakpoint = useBreakpoint();

  const isMobile = breakpoint < Breakpoint.md;

  const isExpanded = !isMobile && isOpen;

  // --------- Helpers

  const handleClick = (theme: Theme) => {
    setTheme(theme);
  };

  const handleToggle = () => {
    const currentIndex = themes.findIndex((t) => t.name === theme);
    const nextIndex = (currentIndex + 1) % themes.length;

    setTheme(themes[nextIndex].name);
  };

  // --------- JSX

  return (
    <div className="relative">
      {/*******************************************/}
      {/* Button Group (Side Menu Open) */}
      {/*******************************************/}

      <div
        className={cn(
          "transform transition-all duration-500",
          isMobile ? "w-full" : isExpanded ? "w-full opacity-100" : "w-10 opacity-0",
        )}
      >
        <div className="flex inline-flex w-full items-center justify-between rounded-lg bg-gray-50 p-1 dark:bg-gray-950/50">
          {themes.map((t) => (
            <button
              key={t.name}
              onClick={() => handleClick(t.name)}
              className={cn(
                "flex size-8 w-9 flex-row items-center justify-center rounded-lg p-2 transition-colors duration-500",
                [theme === t.name ? "bg-white shadow dark:bg-gray-800/70" : "hover:text-gray-900"],
              )}
            >
              <FontAwesomeIcon
                icon={t.icon}
                className="text-gray-700 dark:text-gray-200"
              />
            </button>
          ))}
        </div>
      </div>

      {/*******************************************/}
      {/* Single Button (Side Menu Closed) */}
      {/*******************************************/}

      <div
        className={cn(
          "absolute left-0 top-0 transform transition-all duration-500",
          (isMobile || isExpanded) && "invisible opacity-0",
        )}
      >
        <button
          onClick={handleToggle}
          className={cn(
            "mt-1 flex size-8 flex-row items-center justify-center rounded-lg p-2 transition-colors duration-500",
            theme === "light" ? "bg-white shadow dark:bg-gray-800/70" : "",
            theme === "dark" ? "bg-white shadow dark:bg-gray-800/70" : "",
            theme === "system" ? "bg-white shadow dark:bg-gray-800/70" : "",
            "hover:bg-white dark:hover:bg-gray-800/70",
          )}
        >
          <FontAwesomeIcon
            icon={themes.find((t) => t.name === theme)?.icon || themes[0].icon}
            className="text-gray-700 dark:text-gray-200"
          />
        </button>
      </div>
    </div>
  );
};

export default ThemeSelector;
